import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import {
	PartnerApi,
	PartnerProvisionApi,
	PartnerSearchSelectApi,
	UpdatePartnerOrganizationItemApi,
} from "../../../../../../../../services/Api/Partner/partnerTypes";
import OrganizationPartnerProvisionsTable from "./OrganizationPartnerProvisionsTable";

interface FormProps {
	partners: UpdatePartnerOrganizationItemApi[];
}

interface Props {
	partnerIndex: number;
	partner?: PartnerApi;
	onHide: (data: FormProps) => void;
	form: UseFormReturn<FormProps>;
	handleSave: (data: FormProps) => void;
	updateOrganizationPartners: (obj: PartnerSearchSelectApi) => void;
	onCancel: () => void;
}

const setInitialProvisions = (data: PartnerProvisionApi[]) => {
	try {
		return JSON.parse(JSON.stringify(data));
	} catch (e) {
		return [];
	}
};

const AddOrUpdatePartnerProvisionModal = ({
	form,
	partnerIndex,
	partner,
	onHide,
	handleSave,
	updateOrganizationPartners,
	onCancel,
}: Props) => {
	const {
		control,
		formState: { errors },
		getValues,
		handleSubmit,
	} = form;
	const { t } = useTranslation("admin");
	const [initialProvisions] = useState(setInitialProvisions(getValues(`partners.${partnerIndex}.provisions`)));

	const renderUser = (user: PartnerSearchSelectApi) => {
		return (
			<div className={"d-flex"}>
				{user.name} {user.email ? <small style={{ fontSize: "0.85rem" }}>&nbsp;({user.email})</small> : null}
			</div>
		);
	};

	const searchPartners = (search: string, params: Record<string, any>) => {
		const newParams = {
			...params,
			include: "default_provisions",
			"id|ne":
				getValues("partners")
					.map((x) => x.partner_id)
					.join(FILTER_VALUE_SEPARATOR) || -1,
		};

		return adminApi.getAdminPartnersSearchSelect(search, newParams);
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!getValues(`partners.${partnerIndex}.partner_id`)) {
			onCancel();
		} else {
			handleSave(data);
		}
	});

	const hideModal = (cancel?: boolean) => {
		const values = getValues();
		if (cancel) {
			values.partners[partnerIndex].provisions = initialProvisions;
		}
		onHide(values);
	};

	return (
		<Modal show={true} size={"lg"} onHide={hideModal}>
			<Modal.Header>
				<Modal.Title>
					{partner
						? `${t("modules.organization.field.change_provision_for.title")} ${partner.name}`
						: t("modules.organization.field.add_partner_to_organization.title")}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{!partner ? (
					<FormSelectGroup
						label={"Partner"}
						path={`/partnership/`}
						name={`partners.${partnerIndex}.partner_id`}
						errors={errors}
						defaultValue={partner}
						isClearable={true}
						onChange={(_, obj) => updateOrganizationPartners(obj)}
						formatOptionLabel={renderUser}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
						loadOptions={searchPartners}
						control={control}
						data-testid={`partners.${partnerIndex}.partner_id`}
					/>
				) : null}
				{getValues(`partners.${partnerIndex}.partner_id`) ? (
					<fieldset className={"form-group"}>
						<h5>{t("common.word.provisions")}</h5>
						<OrganizationPartnerProvisionsTable form={form} partnerIndex={partnerIndex} />
					</fieldset>
				) : null}
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={false} type="submit" onClick={onSubmit}>
					{t("common.action.save", { ns: "lib" })}
				</ButtonLoading>
				<Button variant="light" onClick={() => hideModal(true)}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default AddOrUpdatePartnerProvisionModal;
